import * as React from "react";
import { createPortal } from "react-dom";

import styles from "./NewButton.module.css";

export type NewButtonVariant = "primary" | "secondary" | "danger" | "dashed" | "soft" | "green";

export interface NewButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: NewButtonVariant;
  icon?: string;
  block?: boolean;
  accent?: string;
  tooltip?: string;
}

type TipPos = {
  left: number;
  top: number;
  placement: "top" | "bottom";
  /** Смещение стрелки от центра тултипа (px), чтобы всегда указывать на кнопку */
  arrowOffset: number;
};

export const NewButton: React.FC<NewButtonProps> = ({
  variant = "secondary", icon, block, accent, tooltip, className, children, style, onMouseEnter, onMouseLeave, onFocus, onBlur, ...rest
}) => {
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const tipRef = React.useRef<HTMLSpanElement>(null);
  const [tip, setTip] = React.useState<TipPos | null>(null);

  const hideTip = React.useCallback(() => setTip(null), []);

  const showTip = React.useCallback(() => {
    if (!tooltip || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: "top" | "bottom" = spaceBelow < 44 && rect.top > 44 ? "top" : "bottom";
    const top = placement === "bottom" ? rect.bottom + gap : rect.top - gap;
    const anchorX = rect.left + rect.width / 2;
    // Центр тултипа = якорь; позже подправим по реальной ширине, если упрёмся в край
    setTip({ left: anchorX, top, placement, arrowOffset: 0 });
  }, [tooltip]);

  // После монтирования тултипа прижимаем его к краям вьюпорта, стрелку оставляем на кнопке
  React.useLayoutEffect(() => {
    if (!tip || !tipRef.current || !btnRef.current) return;
    const tipEl = tipRef.current;
    const tipWidth = tipEl.offsetWidth;
    const margin = 8;
    const anchorX = btnRef.current.getBoundingClientRect().left + btnRef.current.getBoundingClientRect().width / 2;
    const minCenter = margin + tipWidth / 2;
    const maxCenter = window.innerWidth - margin - tipWidth / 2;
    const left = Math.min(Math.max(anchorX, minCenter), maxCenter);
    const arrowOffset = anchorX - left;
    if (left !== tip.left || arrowOffset !== tip.arrowOffset) {
      setTip((prev) => (prev ? { ...prev, left, arrowOffset } : prev));
    }
  }, [tip]);

  React.useEffect(() => {
    if (!tip) return;
    const onScroll = () => hideTip();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", hideTip);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", hideTip);
    };
  }, [tip, hideTip]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`${styles.btn} ${styles[variant]}${block ? ` ${styles.block}` : ``}${className ? ` ${className}` : ``}`}
        style={accent ? ({ ["--btn-accent" as any]: accent, ...style }) : style}
        aria-label={rest["aria-label"] ?? tooltip}
        {...rest}
        onMouseEnter={(e) => {
          showTip();
          onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          hideTip();
          onMouseLeave?.(e);
        }}
        onFocus={(e) => {
          showTip();
          onFocus?.(e);
        }}
        onBlur={(e) => {
          hideTip();
          onBlur?.(e);
        }}
      >
        {icon && <i className={icon} />}
        {children}
      </button>
      {tooltip && tip && createPortal(
        <span
          ref={tipRef}
          className={`${styles.portalTooltip}${tip.placement === "top" ? ` ${styles.portalTooltipTop}` : ``}`}
          style={{
            left: tip.left,
            top: tip.top,
            ["--tip-arrow-offset" as string]: `${tip.arrowOffset}px`,
          }}
          role="tooltip"
        >
          {tooltip}
        </span>,
        document.body,
      )}
    </>
  );
};
